import React, { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './Paragraph.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';

import {
  getBaseValue,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);

const paragraph = cva(styles.paragraph, {
  variants: {
    size: {
      xs: styles['size--xs'],
      sm: styles['size--sm'],
      md: styles['size--md'],
      lg: styles['size--lg'],
    },
    textAlign: {
      start: styles['align--start'],
      center: styles['align--center'],
      end: styles['align--end'],
    },
    fontWeight: {
      default: styles['font-weight--default'],
      bold: styles['font-weight--bold'],
    },
    truncate: {
      true: styles['truncate--true'],
      false: styles['truncate--false'],
    },
  },
  defaultVariants: {
    size: 'md',
    textAlign: 'start',
  },
});

type ParagraphVariants = VariantProps<typeof paragraph>;
type ParagraphSize = 'xs' | 'sm' | 'md' | 'lg';
type ParagraphTextAlign = 'start' | 'center' | 'end';

interface BaseProps
  extends ParagraphVariants,
    HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
  /**
   * **[Responsive]** The size of the paragraph
   * @default "md"
   */
  size?: ParagraphSize;
  /**
   * The foreground color of the paragraph
   * @default "secondary"
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];
  /**
   * **[Responsive]** The text alignment of the paragraph
   * @default "start"
   */
  textAlign?: ParagraphTextAlign;
  /**
   * Font weight of the paragraph
   * @default "default"
   */
  fontWeight?: 'default' | 'bold';
  /**
   * Truncate text with ellipsis
   * @default false
   */
  truncate?: boolean;
}

// ResponsivePropKeys are the keys of the props that can accept responsive values (base + breakpoint-specific)
type ResponsivePropKeys = 'size' | 'textAlign' | 'truncate';

export type ParagraphProps = WithResponsiveProps<BaseProps, ResponsivePropKeys>;

export const Paragraph = ({
  children,
  size = 'md',
  foregroundColor = 'secondary',
  textAlign = 'start',
  fontWeight = 'default',
  truncate = false,
  ...props
}: ParagraphProps) => {
  // Base values (string) are applied via CVA variants:
  const baseSize = getBaseValue(size, 'md');
  const baseAlign = getBaseValue(textAlign, 'start');
  const truncateBase = getBaseValue(truncate, false);

  // Breakpoint-only classNames come from the helper:
  const sizeResp = getResponsiveClasses(size, 'size');
  const alignResp = getResponsiveClasses(textAlign, 'align');
  const truncateResp = getResponsiveClasses(truncate, 'truncate');

  return (
    <p
      className={[
        paragraph({
          size: baseSize,
          textAlign: baseAlign,
          truncate: truncateBase,
          fontWeight,
        }),
        foregroundColorVariants({ foregroundColor }),
        ...sizeResp, // md/lg/xl/2xl overrides
        ...alignResp,
        ...truncateResp,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </p>
  );
};

export default Paragraph;
